import React, { FormEvent, useContext, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import {
  Box,
  CardActionArea,
  Chip,
  Grid,
  Grid2,
  Modal,
  Typography,
} from '@mui/material';
import {
  IDeviceProps,
  IOnPremiseManagementStatusContactsToNotifyProps,
} from 'services/OnPremiseManagementStatusService/types';
import ComputerIcon from '@mui/icons-material/Computer';
import { getLicenseByIdAsync } from 'services/LicenseService';
import { ILicenseSimpleProps } from 'services/LicenseService/types';
import {
  Check,
  Contacts,
  Description,
  Devices,
  DirectionsCar,
  Phone,
} from '@mui/icons-material';
import ButtonComponent from '../../../../components/ButtonComponent';
import ModalComponent from '../../../../components/ModalComponent';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ChipListComponent from '../../../../components/ChipListComponent';
import TelegramSelectComponent from '../../../../components/TelegramSelectComponent';
import WhatsAppSelectComponent from '../../../../components/WhatsAppSelectComponent';
import EditIcon from '@mui/icons-material/Edit';
import {
  useGetContactsToNotify,
  useUpdateContactsToNotify,
} from '../../../../services/OnPremiseManagementStatusService/hooks/useMutation';
import { AppContext } from '../../../../context/AppContextProvider';

interface CardDeviceServerProps {
  devices: IDeviceProps[];
  license: ILicenseSimpleProps | undefined;
  onPremiseManagementStatusId: number;
  isOnlineApi: boolean;
  isOnlineDeviceServer: boolean;
}

const CardDeviceServer = ({
  devices,
  onPremiseManagementStatusId,
}: CardDeviceServerProps) => {
  const [isOpenLogModal, setIsOpenLogModal] = useState<boolean[]>(
    devices.map((c) => false),
  );
  const { showSnackbar } = useContext(AppContext);

  const [isOpenContactsToNotifyEdition, setIsOpenContactsToNotifyEdition] =
    useState(false);

  const { mutateAsync: mutateGetContactsToNotify } = useGetContactsToNotify();

  const {
    mutateAsync: mutateUpdateContactsToNotify,
    isLoading: isLoadingUpdateContactsToNotify,
  } = useUpdateContactsToNotify();

  const [contactsToNotify, setContactsToNotify] =
    useState<IOnPremiseManagementStatusContactsToNotifyProps>({
      telegramIds: [],
      whatsAppNumbers: [],
    });

  const handleSaveContacts = async () => {
    await mutateUpdateContactsToNotify(
      {
        id: onPremiseManagementStatusId,
        data: contactsToNotify,
      },
      {
        onSuccess: () => {
          showSnackbar('Sucesso ao alterar contatos', 'success');
          setContactsToNotify({ telegramIds: [], whatsAppNumbers: [] });
          setIsOpenContactsToNotifyEdition(false);
        },
      },
    );
  };

  const onClose = () => {
    setIsOpenContactsToNotifyEdition(false);
    setContactsToNotify({ telegramIds: [], whatsAppNumbers: [] });
  };

  const GetContactsToNotifyAsync = () => {
    if (onPremiseManagementStatusId) {
      mutateGetContactsToNotify(onPremiseManagementStatusId).then(
        setContactsToNotify,
      );
    }
  };

  useEffect(() => {
    GetContactsToNotifyAsync();
  }, [onPremiseManagementStatusId]);
  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: '#fff', mb: 3, textAlign: 'center' }}
      >
        Dispositivos do Servidor
      </Typography>

      <ButtonComponent
        startIcon={<Contacts />}
        sx={{ mb: 3 }}
        onClick={() => {
          GetContactsToNotifyAsync();
          setIsOpenContactsToNotifyEdition(true);
        }}
      >
        Editar contatos para notificar
      </ButtonComponent>

      <ModalComponent
        onClose={onClose}
        isOpen={isOpenContactsToNotifyEdition}
        title={'Edição de contatos'}
      >
        <Grid2 container>
          <Grid2 size={{ xs: 12 }} mb={4}>
            <TelegramSelectComponent
              gridSize={12}
              onChange={(telegramIds) =>
                setContactsToNotify((oldV) => ({ ...oldV, telegramIds }))
              }
              value={contactsToNotify.telegramIds}
            />
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <WhatsAppSelectComponent
              gridSize={12}
              onChange={(whatsAppNumbers) =>
                setContactsToNotify((oldV) => ({ ...oldV, whatsAppNumbers }))
              }
              value={contactsToNotify.whatsAppNumbers}
            />
          </Grid2>

          <Grid2 size={{ xs: 12 }} mt={2} justifyContent={'center'}>
            <ButtonComponent
              fullWidth
              loading={isLoadingUpdateContactsToNotify}
              onClick={() => handleSaveContacts()}
            >
              Salvar
            </ButtonComponent>
          </Grid2>
        </Grid2>
      </ModalComponent>

      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Dispositivos ({devices.length})
      </Typography>

      <Grid2 container spacing={2}>
        {devices.map((device, index) => (
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card
              sx={{
                backgroundColor: '#293556',
                p: 2,
                borderRadius: 2,
                minHeight: '140px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {device.deviceName}
                </Typography>

                <Chip
                  icon={<Devices />}
                  label={device.isOnline ? 'Online' : 'Offline'}
                  color={device.isOnline ? 'success' : 'error'}
                  size="small"
                  sx={{ mb: 1 }}
                />

                <Typography
                  sx={{
                    color: '#ccc',
                    fontSize: '0.85rem',
                    mb: 1,
                  }}
                >
                  ID: {device.deviceId}
                </Typography>

                <Typography
                  sx={{
                    color: '#ccc',
                    fontSize: '0.8rem',
                  }}
                >
                  {device.lastEvent ? (
                    <>
                      Último Evento{' '}
                      {new Date(
                        new Date(device.lastEvent).getTime() +
                          3 * 60 * 60 * 1000,
                      ).toLocaleString('pt-BR')}
                    </>
                  ) : (
                    'Nenhum evento registrado.'
                  )}
                </Typography>
              </Box>

              <Box marginTop={2} justifyContent={'flex-end'} display="flex">
                <ButtonComponent
                  startIcon={<Description />}
                  onClick={() => {
                    setIsOpenLogModal(
                      isOpenLogModal.map((c, idx) => idx === index),
                    );
                  }}
                  size={'small'}
                >
                  Ver Logs
                </ButtonComponent>
              </Box>
              <ModalComponent
                onClose={() =>
                  setIsOpenLogModal(isOpenLogModal.map(() => false))
                }
                isOpen={isOpenLogModal[index]}
                title={`Logs Recentes (${device.logs.length})`}
              >
                {device.logs && device.logs.length > 0 && (
                  <Box>
                    {device.logs.map((log, logIndex) => (
                      <Typography
                        key={logIndex}
                        sx={{
                          fontSize: '0.75rem',
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.description}
                      </Typography>
                    ))}
                  </Box>
                )}
              </ModalComponent>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

export default CardDeviceServer;
